import Axios from 'axios';
import { showError } from '../../utils/message';

// Keywords để detect usage exhausted errors
const USAGE_EXHAUSTED_KEYWORDS = ['sử dụng hết', 'hết lượt', 'usage', 'quota', 'limit', 'exceeded'];

// Lazy load userFeaturesStore to avoid circular dependency
// (restClient -> userFeaturesStore -> subscriptionService -> subscription.repository -> BaseRepository -> restClient)
let userFeaturesStore = null;
const getUserFeaturesStore = async () => {
  if (!userFeaturesStore) {
    const module = await import('../../store/userFeaturesStore');
    userFeaturesStore = module.default;
  }
  return userFeaturesStore;
};

// Merge params to axios config
function mergeAxiosConfig(data, config) {
  const axiosConfig = Object.assign(config || {}, {
    params: data,
    // Config for query array
    paramsSerializer: {
      indexes: null,
    },
  });
  return axiosConfig;
}

export default class RestClient {
  constructor(servicePath) {
    this.client = this.createAxiosClient();
    this.servicePath = servicePath;
  }

  createAxiosClient() {
    const axiosInstance = Axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
      headers: {
        'Content-type': 'application/json',
      },
      withCredentials: false,
    });

    axiosInstance.interceptors.request.use(this.onRequest, this.onRequestError);
    axiosInstance.interceptors.response.use(this.onResponse, this.onResponseError);

    return axiosInstance;
  }

  onRequest = async config => {
    // Add token to headers if available
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // If data is FormData, remove Content-Type header to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  };

  onRequestError(error) {
    return Promise.reject(error);
  }

  onResponse = response => {
    // Do something here after response
    return response;
  };

  onResponseError = error => {
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      // Don't redirect if it's a trial eligibility check (user might not be logged in)
      const isTrialCheck = error.config?.url?.includes('/payment/trial/check');

      if (!isTrialCheck) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }

    // Handle 403 Forbidden - check if related to usage limit and refresh user features
    if (error.response?.status === 403) {
      const message = error.response?.data?.message;
      if (message) {
        const messageLower = message.toLowerCase();
        const isUsageError = USAGE_EXHAUSTED_KEYWORDS.some(keyword =>
          messageLower.includes(keyword.toLowerCase())
        );

        if (isUsageError) {
          // Force refresh user features to update UI state (using async loader to avoid circular dep)
          getUserFeaturesStore().then(store => {
            store.getState().fetchUserFeatures(true);
          });
        }
      }
    }

    return Promise.reject(error);
  };

  request(method, url, data, config) {
    return new Promise((resolve, reject) => {
      const axiosConfig = Object.assign(config, {
        url: `${this.servicePath}${url}`,
        method,
        data,
      });
      
      this.client.request(axiosConfig)
        .then(response => {
          const result = response.data;

          // Check if API response has error code (code !== 0)
          if (result.code !== 0) {
            // Handle 401 Unauthorized from payload.code
            if (result.code === 401) {
              const isTrialCheck = axiosConfig.url?.includes('/payment/trial/check');
              if (!isTrialCheck) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                if (window.location.pathname !== '/login') {
                  window.location.href = '/login';
                  return;
                }
              }
            }

            if (result.message) {
              showError(result.message);
            }
            reject(result);
          } else {
            resolve(result);
          }
        })
        .catch(error => {
          if (Axios.isAxiosError(error)) {
            if (error.response) {
              // The request was made and the server responded with a status code
              // that falls out of the range of 2xx
              const isTrialCheck = error.config?.url?.includes('/payment/trial/check');
              const is401 = error.response.status === 401;

              if (error.response.data) {
                const serviceErrorResponse = error.response.data;
                // Don't show error message for 401 on trial check (user not logged in)
                if (serviceErrorResponse.message && !(isTrialCheck && is401)) {
                  // Ensure message is a string
                  const errorMsg = typeof serviceErrorResponse.message === 'string'
                    ? serviceErrorResponse.message
                    : 'Có lỗi xảy ra';
                  showError(errorMsg);
                }
                reject(serviceErrorResponse);
              } else {
                reject(error);
              }
            } else if (error.request) {
              // The request was made but no response was received
              showError('Không thể kết nối đến server');
              reject(error);
            } else {
              // Something happened in setting up the request that triggered an Error
              reject(error);
            }
          } else {
            reject(error);
          }
        });
    });
  }

  get(url, data, config) {
    const axiosConfig = mergeAxiosConfig(data, config);
    return this.request('GET', url, null, axiosConfig);
  }

  post(url, data, config) {
    const axiosConfig = mergeAxiosConfig(data, config);
    delete axiosConfig.params;
    return this.request('POST', url, data, axiosConfig);
  }

  put(url, data, config) {
    const axiosConfig = mergeAxiosConfig(data, config);
    delete axiosConfig.params;
    return this.request('PUT', url, data, axiosConfig);
  }

  delete(url, data, config) {
    const axiosConfig = mergeAxiosConfig(data, config);
    delete axiosConfig.params;
    return this.request('DELETE', url, data, axiosConfig);
  }
}

